import { ISaveable } from '../../../../../stores/types';
import { ICredentialsStore } from '../integration-store';
import { INTEGRATION_CODE, IOAuthCredentialsParams } from "../../../../../models/IntegrationsTypes";
import { makeAutoObservable } from "mobx";
import { rootContainer, TYPE } from "../../../../../services/container/root";
import IntegrationsService from '../../../../../services/api/integrations-service';

export interface INetSuiteCredentials {
    accountId: string,
    clientId: string,
    clientSecret: string;
    locationId: string;
    locationName: string;
}

interface IEventMessage {
    type: string;
    [payloadKey: string]: any;
}

const CLOSE_INTERVAL = 50;

class NetSuiteCredentialsStore implements ISaveable, ICredentialsStore {
    private _changes: INetSuiteCredentials = {} as INetSuiteCredentials;
    private _window = {} as Window;
    private _intervalId: any = undefined;
    private _messageHandler?: (event: IEventMessage) => void;
    private _serviceCode = INTEGRATION_CODE.NET_SUITE;

    private get IntegrationsService(): IntegrationsService {
        return rootContainer.get<IntegrationsService>(TYPE.IntegrationsService);
    }

    constructor(private _credentialsParams: IOAuthCredentialsParams) {
        makeAutoObservable(this);
    }

    public get accountId() { return this._changes.accountId; }
    public set accountId(v: string) { this._changes.accountId = v; }

    public get clientId() { return this._changes.clientId; }
    public set clientId(v: string) { this._changes.clientId = v; }

    public get clientSecret() { return this._changes.clientSecret; }
    public set clientSecret(v: string) { this._changes.clientSecret = v; }

    public get locationId() { return this._changes.locationId; }
    public set locationId(v: string) { this._changes.locationId = v; }

    public get locationName() { return this._changes.locationName; }
    public set locationName(v: string) { this._changes.locationName = v; }

    // Handle case when user didn't finish authorization flow and closed window
    private _listenToWindowClosed(reject: (reason?: any) => void) {
        /** make sure to always clear interval before starting new one */
        clearInterval(this._intervalId);

        this._intervalId = setInterval(() => {
            if (!this._window.closed) {
                return;
            }
            clearInterval(this._intervalId);
            reject(new Error('Authorization interrupted, window was closed before finishing the process'));
        }, CLOSE_INTERVAL);
    }

    private _handleMessage(event: IEventMessage, oauth_token_secret: string, resolve: (value: unknown) => void, reject: (reason?: any) => void) {
        if (event.data?.type !== 'OAUTH_RESPOSE') {
            return;
        }

        this._window.close();
        window.removeEventListener('message', this._messageHandler as (event: IEventMessage) => void, false);

        const { oauth_token, oauth_verifier } = event.data?.payload || {};

        if (oauth_token) {
            return resolve({
                oauth_token,
                oauth_verifier,
                oauth_token_secret,
                account_id: this.accountId,
                client_id: this.clientId,
                client_secret: this.clientSecret,
                location_id: this.locationId,
                location_name: this.locationName
            });
        }

        return reject(new Error('Failed to receive oauthToken'));
    }

    private async getOauth1Token(): Promise<{ oauth_token: string; oauth_token_secret: string }> {
        const { callback_url, nonce } = this._credentialsParams;
        const { accountId, clientId, clientSecret } = this._changes;
        const body = {
            serviceCode: this._serviceCode,
            redirectUri: callback_url,
            nonce,
            accountId,
            clientId,
            clientSecret,
        };

        return this.IntegrationsService.requestOauth1Token(body);
    }

    private buildOauth1Url(oauthToken: string): string {
        const { oauth_url } = this._credentialsParams;
        return `https://${this.accountId}.${oauth_url}?oauth_token=${oauthToken}`;
    }

    /** ICredentialsStore implementation ************/
    public async getCredentials() {

        if (this._messageHandler) {
            window.removeEventListener('message', this._messageHandler, false);
        }

        const { oauth_token, oauth_token_secret } = await this.getOauth1Token();
        const url = this.buildOauth1Url(oauth_token);
        return new Promise((resolve, reject) => {

            this._window = window.open(url, '', 'width=800,height=600,resizeable,scrollbars') as Window;

            this._messageHandler = (event: IEventMessage) => {
                this._handleMessage(event, oauth_token_secret, resolve, reject);
            };

            window.addEventListener('message', this._messageHandler, false);

            this._listenToWindowClosed(reject);
        });
    }

    /********* ISaveable implementation *************/
    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    public discardChanges(): void {
        this._changes = {} as INetSuiteCredentials;
    }

    public getUpdatedModel(): INetSuiteCredentials { throw new Error('Not implemented.'); }
    public isSaving(): boolean { throw new Error('Not implemented.'); }
    public commitChanges() { throw new Error('Not implemented.'); }
    public async save() { throw new Error('Not implemented.'); }
}

export default NetSuiteCredentialsStore;
